import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Input,
  Row,
  Upload,
  notification,
  Space,
  Table,
  Spin,
  PageHeader,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import FileSaver from "file-saver";
import util from "helpers/util";
import itemTakeApi from "constants/api/itemTake";
import moment from "moment";
import { useLocation } from "react-router-dom/cjs/react-router-dom";

export default function UploadStockTakeResult() {
  const tbListSearchInput = useRef(null);
  const [tbListSearchText, setTbListSearchText] = useState("");
  const [tbListSearchedColumn, setTbListSearchedColumn] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isForceRefresh, setIsForceRefresh] = useState();
  const [listUploadStockResult, setListUploadStockResult] = useState([]);

  const location = useLocation();

  useEffect(() => {
    if (isForceRefresh !== undefined && !isForceRefresh) {
      return;
    }
      
    setIsLoading(true);

    itemTakeApi.getListItemTakeUpload(location.state.token)
    .then((res) => {
      if (res.success) {
        let newRows = [];

        res.result.map((row) => {
          newRows.push({
            token: row.token,
            filePath: row.filePath,
            uploadedFile: row.uploadedFile,
            errorFile: row.errorFile,
            successFile: row.successFile,
            createdDate: util.formatNullableDatetime(row.createdDate),
          });
        });

        setListUploadStockResult(newRows);
      }
      else {
        notification.error({
          message: "Failed",
          description: res.message,
          duration: 0,
        });
      }
    })
    .catch((err) => {
      util.displayApiError(err);
    })
    .finally(() => {
      setIsLoading(false);
      setIsForceRefresh(false);
    });
  }, [isForceRefresh])

  const handleSampleFileClick = () => {
    FileSaver.saveAs(
      process.env.PUBLIC_URL + "/assets/samplefile/sample_item_take_upload_result.xlsx",
      "sample_item_take_upload_result.xlsx"
    );
  };

  const handleDownloadFileById = (uploadedFile, fileType, fileName) => {
    setIsLoading(true);

    const data = {
      uploadedFile: uploadedFile,
      fileType: fileType,
    };

    itemTakeApi.getFileItemTakeUploadResult(data)
      .then((res) => {
        if (res.data.size < 100) {
          notification.error({
            message: "Failed",
            description: "File not exist",
            duration: 0,
          });

          return;
        }

        const fileExtension = fileName.split(".").pop().toLowerCase();
        let fileType;

        if (fileExtension === "xlsx") {
          fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8";
        }
        else {
          fileType = "text/csv";
        }
        
        const blob = new Blob([res.data], {
          type: fileType,
        });

        const url = window.URL.createObjectURL(blob);
        FileSaver.saveAs(url, `${fileName}`);
      })
      .catch((err) => {
        util.displayApiError(err);
      })
      .finally(() => setIsLoading(false));
  };

  const tbListHandleReset = (clearFilters) => {
    clearFilters();
    setTbListSearchText("");
  };

  const tbListHandleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setTbListSearchText(selectedKeys[0]);
    setTbListSearchedColumn(dataIndex);
  };

  const tbListGetColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={tbListSearchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            tbListHandleSearch(selectedKeys, confirm, dataIndex)
          }
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              tbListHandleSearch(selectedKeys, confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => tbListHandleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setTbListSearchText(selectedKeys[0]);
              setTbListSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    // eslint-disable-next-line react/display-name
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    // onFilterDropdownVisibleChange: (visible) => {
    //   if (visible) {
    //     // setTimeout(() => tbStoreSearchInput.select(), 100);
    //   }
    // },
    render: (text) =>
      tbListSearchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[tbListSearchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columnsTb = [
    {
      title: "Token",
      dataIndex: "token",
      ...tbListGetColumnSearchProps("token"),
      sorter: (a, b) => a.token.localeCompare(b.token),
    },
    {
      title: "Original File",
      dataIndex: "uploadedFile",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() =>
            handleDownloadFileById(record.uploadedFile, "original", text)
          }
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Success File",
      dataIndex: "successFile",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() =>
            handleDownloadFileById(record.uploadedFile, "success", text)
          }
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Error File",
      dataIndex: "errorFile",
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => handleDownloadFileById(record.uploadedFile, "error", text)}
        >
          {text}
        </Button>
      ),
    },
    {
      title: "Upload Date",
      dataIndex: "createdDate",
      ...tbListGetColumnSearchProps("createdDate"),
      sorter: (a, b) =>
        moment(a.createdDate).unix() - moment(b.createdDate).unix(),
    },
  ];

  const handleUpload = (uploadInfo) => {
    setIsLoading(true);

    if (uploadInfo.fileList.length === 0) {
      setIsLoading(false);

      notification.error({
        message: "Error",
        description: "No file to upload",
        duration: 0,
      });

      return;
    }

    let formData = new FormData();
    formData.append("file", uploadInfo.file);
    formData.append("token", location.state.token);

    itemTakeApi.uploadItemResultExcel(formData)
    .then((result) => {
      if (result.data.success) {
        notification.success({
          message: "Success",
          description: result.data.message,
          duration: 0,
        });
      }
      else {
        notification.error({
          message: "Failed",
          description: result.data.message,
          duration: 0,
        });
      }
    })
    .catch((err) => {
      util.displayApiError(err);
    })
    .finally(() => {
      setIsForceRefresh(true);
    });
  }

  return (
    <>
      <PageHeader title="Upload Stock Take Result" />
      <br />
      <Spin tip="Loading..." size="large" spinning={isLoading}>
        <Row>
          {/* <Col span={24}> */}
          <Space size="middle" align="start">
            <Button
              size="large"
              icon={<DownloadOutlined />}
              onClick={() => handleSampleFileClick()}
            >
              Sample File
            </Button>

            <Upload
              name="file"
              beforeUpload={() => false}
              // onRemove={() => setRowsStore([])}
              multiple={false}
              maxCount={1}
              accept=".xlsx"
              onChange={handleUpload}
            >
              <Button size="large" type="dashed" icon={<UploadOutlined />}>
                Upload File
              </Button>
              {/* <br />
              <Text type="danger">Max Data 1000 Rows</Text> */}
            </Upload>
          </Space>
          {/* </Col> */}
        </Row>
        <br />
        <br />
        <Row>
          <Col span={24}>
            <Table
              size="small"
              //loading={isLoadingTb}
              columns={columnsTb}
              dataSource={listUploadStockResult}
              pagination={{ pageSize: 5 }}
              // pagination={false}
              // scroll={{ y: 200 }}
              bordered
            />
          </Col>
        </Row>
      </Spin>
    </>
  );
}
